import React from 'react';
import { Box } from '@mui/material';
import type { CourseTeeSet, CourseHoleInfo, scoreHole } from '../../../shared/config/iTournamentBuilder';
import type { ScorecardGridConfig } from './scorecardTemplateTypes';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ScorecardGridProps {
    config: ScorecardGridConfig;
    /** Hole par / distance / handicap source. Null when no course data is loaded. */
    teeSet: CourseTeeSet | null;
    /** One holeScores array per player in the scoring unit (index matches playerLabels). */
    playerScores: scoreHole[][];
    /** Display label for each player row (e.g. individual name or "Player"). */
    playerLabels: string[];
    /** Hole scores for the marker (read playerScore field for each hole). */
    markerScores: scoreHole[];
    markerLabel: string;
    /**
     * Actual number of players in the scoring unit — used when
     * config.playerRows === 'auto' to determine how many score rows to render.
     */
    playerCount: number;
    /** displayWidth / SCORECARD_CANVAS_W — scales fonts, gap, and padding. */
    scaleFactor: number;
    /** When true, synthetic sample data is displayed instead of real data. */
    isSampleData?: boolean;
    /** When set, highlights this hole column in light yellow and circles the header number. */
    highlightHoleNumber?: number | null;
    /** When true, suppresses placeholder labels (e.g. "Marker", "Player N") in score rows. */
    blankMode?: boolean;
}

// ─── Sample data ─────────────────────────────────────────────────────────────

const SAMPLE_HOLES: CourseHoleInfo[] = [
    { number: 1,  par: 4, dist: 385, hdcp: 11 },
    { number: 2,  par: 3, dist: 162, hdcp: 17 },
    { number: 3,  par: 4, dist: 401, hdcp: 3  },
    { number: 4,  par: 5, dist: 521, hdcp: 7  },
    { number: 5,  par: 4, dist: 368, hdcp: 15 },
    { number: 6,  par: 3, dist: 178, hdcp: 9  },
    { number: 7,  par: 4, dist: 412, hdcp: 1  },
    { number: 8,  par: 5, dist: 498, hdcp: 13 },
    { number: 9,  par: 4, dist: 356, hdcp: 5  },
    { number: 10, par: 4, dist: 394, hdcp: 6  },
    { number: 11, par: 3, dist: 155, hdcp: 18 },
    { number: 12, par: 4, dist: 388, hdcp: 10 },
    { number: 13, par: 5, dist: 533, hdcp: 4  },
    { number: 14, par: 4, dist: 372, hdcp: 14 },
    { number: 15, par: 3, dist: 191, hdcp: 8  },
    { number: 16, par: 4, dist: 406, hdcp: 2  },
    { number: 17, par: 5, dist: 511, hdcp: 12 },
    { number: 18, par: 4, dist: 361, hdcp: 16 },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function scoreForHole(scores: scoreHole[], holeNumber: number): number | null {
    const s = scores.find((h) => h.holeNumber === holeNumber);
    return s && s.playerScore > 0 ? s.playerScore : null;
}

function hcpAdjForHole(scores: scoreHole[], holeNumber: number): number {
    return scores.find((h) => h.holeNumber === holeNumber)?.playerHcpAdj ?? 0;
}

function sumScores(scores: scoreHole[], holes: CourseHoleInfo[]): number | null {
    let total = 0;
    let hasAny = false;
    for (const h of holes) {
        const s = scores.find((sh) => sh.holeNumber === h.number);
        if (s && s.playerScore > 0) {
            total += s.playerScore;
            hasAny = true;
        }
    }
    return hasAny ? total : null;
}

function strokeDots(adj: number): string {
    if (adj <= 0) return '';
    if (adj === 1) return '•';
    if (adj === 2) return '••';
    return '•••';
}

// ─── Cell style factories ─────────────────────────────────────────────────────

function baseCell(sf: number, extra?: React.CSSProperties): React.CSSProperties {
    return {
        border: '0.5px solid #c8c8c8',
        textAlign: 'center',
        verticalAlign: 'middle',
        padding: `${1 * sf}px ${2 * sf}px`,
        fontSize: `${9 * sf}px`,
        lineHeight: 1.1,
        ...extra,
    };
}

function labelCell(sf: number, extra?: React.CSSProperties): React.CSSProperties {
    return {
        border: '0.5px solid #c8c8c8',
        textAlign: 'left',
        verticalAlign: 'middle',
        padding: `${1 * sf}px ${3 * sf}px`,
        fontSize: `${8 * sf}px`,
        lineHeight: 1.1,
        whiteSpace: 'nowrap',
        ...extra,
    };
}

const COLORS = {
    headerBg:     '#e4e4e4',
    subtotalBg:   '#efefef',
    parBg:        '#ffffff',
    hdcpBg:       '#f7f7f7',
    distBg:       '#ffffff',
    playerBg:     '#ffffff',
    playerAltBg:  '#f3f3f3',
    markerBg:     '#ffffff',
    foldGapBg:    '#f9f9f9',
    scoreInk:     'rgba(30, 78, 55, 1)',
    startHoleHighlight: '#FFF9C4',
};

function cellBg(base: string, holeNumber: number, highlightHole?: number | null): string {
    return holeNumber === highlightHole ? COLORS.startHoleHighlight : base;
}

function HoleNumberHeader({ number, sf, highlightHole }: { number: number; sf: number; highlightHole?: number | null }) {
    if (number !== highlightHole) return <>{number}</>;
    const size = Math.max(14 * sf, 10);
    return (
        <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: size,
            height: size,
            minWidth: size,
            borderRadius: '50%',
            border: `${Math.max(1, 1.5 * sf)}px solid #333`,
            fontWeight: 'bold',
            lineHeight: 1,
        }}>
            {number}
        </span>
    );
}

// ─── Fold-gap cell ────────────────────────────────────────────────────────────

function FoldGapCell({ sf, foldGapPx }: { sf: number; foldGapPx: number }) {
    return (
        <td
            style={{
                width: foldGapPx * sf,
                minWidth: foldGapPx * sf,
                maxWidth: foldGapPx * sf,
                backgroundColor: COLORS.foldGapBg,
                borderTop: 'none',
                borderBottom: 'none',
                borderLeft: `${0.5 * sf}px dashed #bbb`,
                borderRight: `${0.5 * sf}px dashed #bbb`,
                padding: 0,
            }}
        />
    );
}

function rowLabel(label: string, fallback: string, blankMode: boolean): string {
    if (blankMode) return label;
    return label || fallback;
}

// ─── Main component ───────────────────────────────────────────────────────────

const ScorecardGrid: React.FC<ScorecardGridProps> = ({
    config,
    teeSet,
    playerScores,
    playerLabels,
    markerScores,
    markerLabel,
    playerCount,
    scaleFactor: sf,
    isSampleData = false,
    highlightHoleNumber = null,
    blankMode = false,
}) => {
    const holes: CourseHoleInfo[] = isSampleData
        ? SAMPLE_HOLES
        : (teeSet?.holes ?? SAMPLE_HOLES);

    const front9 = holes.filter((h) => h.number >= 1 && h.number <= 9);
    const back9  = holes.filter((h) => h.number >= 10 && h.number <= 18);

    const outPar   = front9.reduce((s, h) => s + h.par, 0);
    const inPar    = back9.reduce((s,  h) => s + h.par, 0);
    const totalPar = outPar + inPar;

    const outDist   = front9.reduce((s, h) => s + (h.dist ?? 0), 0);
    const inDist    = back9.reduce((s,  h) => s + (h.dist ?? 0), 0);
    const totalDist = outDist + inDist;

    const actualPlayerCount = config.playerRows === 'auto'
        ? playerCount
        : (config.playerRows as number);

    const highlightHole = highlightHoleNumber;
    const displayMarkerLabel = rowLabel(markerLabel, 'Marker', blankMode);

    const showFold  = config.showFoldGap && back9.length > 0;
    const foldPx    = config.foldGapPx ?? 16;
    // Explicit row height for player/marker rows; undefined = size-to-content.
    const rowHeight = config.playerRowHeightPx != null
        ? config.playerRowHeightPx * sf
        : undefined;

    // ── Hole number header row ────────────────────────────────────────────────
    const subtotalStyle = (bg: string, extra?: React.CSSProperties) =>
        baseCell(sf, { backgroundColor: bg, fontWeight: 'bold', ...extra });

    // All score columns (holes + subtotals) share the same fixed width.
    // The label column absorbs whatever space remains.
    const SUBTOTAL_W = 32 * sf;
    const HOLE_W     = SUBTOTAL_W;

    return (
        <Box sx={{ width: '100%', height: '100%', overflow: 'hidden', boxSizing: 'border-box' }}>
            <table
                style={{
                    borderCollapse: 'collapse',
                    width: '100%',
                    fontFamily: "'Fredoka', sans-serif",
                    tableLayout: 'fixed',
                }}
            >
                {/* ── Column width declarations ─────────────────────────────── */}
                {/* With table-layout:fixed these pins force all 18 hole columns  */}
                {/* to share remaining space equally regardless of cell content.  */}
                <colgroup>
                    <col /> {/* label — takes remaining space */}
                    {front9.map((h) => <col key={`f${h.number}`} style={{ width: HOLE_W }} />)}
                    <col style={{ width: SUBTOTAL_W }} />
                    {showFold && <col style={{ width: foldPx * sf }} />}
                    {back9.map((h) => <col key={`b${h.number}`} style={{ width: HOLE_W }} />)}
                    {back9.length > 0 && <col style={{ width: SUBTOTAL_W }} />}
                    <col style={{ width: SUBTOTAL_W }} />
                </colgroup>

                {/* ── Hole number header ──────────────────────────────────── */}
                <thead>
                    <tr>
                        <td style={labelCell(sf, { backgroundColor: COLORS.headerBg, fontWeight: 'bold', fontSize: `${8.5 * sf}px` })}>
                            Hole
                        </td>
                        {front9.map((h) => (
                            <td key={h.number} style={baseCell(sf, {
                                backgroundColor: cellBg(COLORS.headerBg, h.number, highlightHole),
                                fontWeight: 'bold',
                                fontSize: `${8.5 * sf}px`,
                            })}>
                                <HoleNumberHeader number={h.number} sf={sf} highlightHole={highlightHole} />
                            </td>
                        ))}
                        <td style={subtotalStyle(COLORS.headerBg)}>Out</td>
                        {showFold && <FoldGapCell sf={sf} foldGapPx={foldPx} />}
                        {back9.map((h) => (
                            <td key={h.number} style={baseCell(sf, {
                                backgroundColor: cellBg(COLORS.headerBg, h.number, highlightHole),
                                fontWeight: 'bold',
                                fontSize: `${8.5 * sf}px`,
                            })}>
                                <HoleNumberHeader number={h.number} sf={sf} highlightHole={highlightHole} />
                            </td>
                        ))}
                        {back9.length > 0 && <td style={subtotalStyle(COLORS.headerBg)}>In</td>}
                        <td style={subtotalStyle(COLORS.headerBg)}>Tot</td>
                    </tr>
                </thead>

                <tbody>
                    {/* ── Par ──────────────────────────────────────────────── */}
                    {config.showPar && (
                        <tr>
                            <td style={labelCell(sf, { backgroundColor: COLORS.parBg })}>Par</td>
                            {front9.map((h) => (
                                <td key={h.number} style={baseCell(sf, { backgroundColor: cellBg(COLORS.parBg, h.number, highlightHole) })}>
                                    {h.par}
                                </td>
                            ))}
                            <td style={subtotalStyle(COLORS.subtotalBg)}>{outPar}</td>
                            {showFold && <FoldGapCell sf={sf} foldGapPx={foldPx} />}
                            {back9.map((h) => (
                                <td key={h.number} style={baseCell(sf, { backgroundColor: cellBg(COLORS.parBg, h.number, highlightHole) })}>
                                    {h.par}
                                </td>
                            ))}
                            {back9.length > 0 && <td style={subtotalStyle(COLORS.subtotalBg)}>{inPar}</td>}
                            <td style={subtotalStyle(COLORS.subtotalBg)}>{totalPar}</td>
                        </tr>
                    )}

                    {/* ── Handicap ─────────────────────────────────────────── */}
                    {config.showHandicap && (
                        <tr>
                            <td style={labelCell(sf, { backgroundColor: COLORS.hdcpBg })}>Hdcp</td>
                            {front9.map((h) => (
                                <td key={h.number} style={baseCell(sf, { backgroundColor: cellBg(COLORS.hdcpBg, h.number, highlightHole) })}>
                                    {h.hdcp ?? '—'}
                                </td>
                            ))}
                            <td style={subtotalStyle(COLORS.subtotalBg)} />
                            {showFold && <FoldGapCell sf={sf} foldGapPx={foldPx} />}
                            {back9.map((h) => (
                                <td key={h.number} style={baseCell(sf, { backgroundColor: cellBg(COLORS.hdcpBg, h.number, highlightHole) })}>
                                    {h.hdcp ?? '—'}
                                </td>
                            ))}
                            {back9.length > 0 && <td style={subtotalStyle(COLORS.subtotalBg)} />}
                            <td style={subtotalStyle(COLORS.subtotalBg)} />
                        </tr>
                    )}

                    {/* ── Distance ─────────────────────────────────────────── */}
                    {config.showDistance && (
                        <tr>
                            <td style={labelCell(sf, { backgroundColor: COLORS.distBg })}>Dist</td>
                            {front9.map((h) => (
                                <td key={h.number} style={baseCell(sf, { backgroundColor: cellBg(COLORS.distBg, h.number, highlightHole), overflow: 'hidden' })}>
                                    {h.dist ?? '—'}
                                </td>
                            ))}
                            <td style={subtotalStyle(COLORS.subtotalBg)}>
                                {outDist > 0 ? outDist : ''}
                            </td>
                            {showFold && <FoldGapCell sf={sf} foldGapPx={foldPx} />}
                            {back9.map((h) => (
                                <td key={h.number} style={baseCell(sf, { backgroundColor: cellBg(COLORS.distBg, h.number, highlightHole), overflow: 'hidden' })}>
                                    {h.dist ?? '—'}
                                </td>
                            ))}
                            {back9.length > 0 && (
                                <td style={subtotalStyle(COLORS.subtotalBg, { overflow: 'hidden' })}>
                                    {inDist > 0 ? inDist : ''}
                                </td>
                            )}
                            <td style={subtotalStyle(COLORS.subtotalBg, { overflow: 'hidden' })}>
                                {totalDist > 0 ? totalDist : ''}
                            </td>
                        </tr>
                    )}

                    {/* ── Player score rows ─────────────────────────────────── */}
                    {config.showPlayer &&
                        Array.from({ length: actualPlayerCount }).map((_, playerIdx) => {
                            const scores  = isSampleData ? [] : (playerScores[playerIdx] ?? []);
                            const label   = rowLabel(
                                playerLabels[playerIdx] ?? '',
                                `Player ${playerIdx + 1}`,
                                blankMode,
                            );
                            const rowBg   = playerIdx % 2 === 0 ? COLORS.playerBg : COLORS.playerAltBg;
                            const outScore  = isSampleData ? null : sumScores(scores, front9);
                            const inScore   = isSampleData ? null : sumScores(scores, back9);
                            const totalScore = outScore != null && inScore != null
                                ? outScore + inScore
                                : outScore ?? inScore ?? null;
                            const rhStyle = rowHeight != null ? { height: rowHeight } : {};

                            return (
                                <React.Fragment key={`player-${playerIdx}`}>
                                    <tr>
                                        <td
                                            style={labelCell(sf, {
                                                backgroundColor: rowBg,
                                                fontWeight: 'bold',
                                                width: `${60 * sf}px`,
                                                maxWidth: `${60 * sf}px`,
                                                ...rhStyle,
                                            })}
                                            title={label}
                                        >
                                            <div style={{
                                                width: `${60 * sf}px`,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}>
                                                {label}
                                            </div>
                                        </td>
                                        {front9.map((h) => {
                                            const score = isSampleData ? null : scoreForHole(scores, h.number);
                                            const adj   = isSampleData ? 0 : hcpAdjForHole(scores, h.number);
                                            return (
                                                <td key={h.number} style={baseCell(sf, {
                                                    backgroundColor: cellBg(rowBg, h.number, highlightHole),
                                                    position: 'relative',
                                                    ...(score != null ? { fontWeight: 600, color: COLORS.scoreInk } : {}),
                                                    ...rhStyle,
                                                })}>
                                                    {score ?? ''}
                                                    {config.showStrokes && adj > 0 && (
                                                        <span style={{
                                                            fontSize: `${6 * sf}px`,
                                                            verticalAlign: 'super',
                                                            color: '#c00',
                                                            marginLeft: `${1 * sf}px`,
                                                        }}>
                                                            {strokeDots(adj)}
                                                        </span>
                                                    )}
                                                </td>
                                            );
                                        })}
                                        <td style={subtotalStyle(COLORS.subtotalBg, {
                                            ...(outScore != null ? { fontWeight: 600, color: COLORS.scoreInk } : {}),
                                            ...rhStyle,
                                        })}>
                                            {outScore ?? ''}
                                        </td>
                                        {showFold && <FoldGapCell sf={sf} foldGapPx={foldPx} />}
                                        {back9.map((h) => {
                                            const score = isSampleData ? null : scoreForHole(scores, h.number);
                                            const adj   = isSampleData ? 0 : hcpAdjForHole(scores, h.number);
                                            return (
                                                <td key={h.number} style={baseCell(sf, {
                                                    backgroundColor: cellBg(rowBg, h.number, highlightHole),
                                                    position: 'relative',
                                                    ...(score != null ? { fontWeight: 600, color: COLORS.scoreInk } : {}),
                                                    ...rhStyle,
                                                })}>
                                                    {score ?? ''}
                                                    {config.showStrokes && adj > 0 && (
                                                        <span style={{
                                                            fontSize: `${6 * sf}px`,
                                                            verticalAlign: 'super',
                                                            color: '#c00',
                                                            marginLeft: `${1 * sf}px`,
                                                        }}>
                                                            {strokeDots(adj)}
                                                        </span>
                                                    )}
                                                </td>
                                            );
                                        })}
                                        {back9.length > 0 && (
                                            <td style={subtotalStyle(COLORS.subtotalBg, {
                                                ...(inScore != null ? { fontWeight: 600, color: COLORS.scoreInk } : {}),
                                                ...rhStyle,
                                            })}>
                                                {inScore ?? ''}
                                            </td>
                                        )}
                                        <td style={subtotalStyle(COLORS.subtotalBg, {
                                            ...(totalScore != null ? { fontWeight: 600, color: COLORS.scoreInk } : {}),
                                            ...rhStyle,
                                        })}>
                                            {totalScore ?? ''}
                                        </td>
                                    </tr>
                                </React.Fragment>
                            );
                        })}

                    {/* ── Marker score row ──────────────────────────────────── */}
                    {config.showMarker && (
                        <tr>
                            <td
                                style={labelCell(sf, {
                                    backgroundColor: COLORS.markerBg,
                                    fontWeight: 'bold',
                                    width: `${60 * sf}px`,
                                    maxWidth: `${60 * sf}px`,
                                    ...(rowHeight != null ? { height: rowHeight } : {}),
                                })}
                                title={displayMarkerLabel}
                            >
                                <div style={{
                                    width: `${60 * sf}px`,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}>
                                    {displayMarkerLabel}
                                </div>
                            </td>
                            {front9.map((h) => {
                                const score = isSampleData ? null : scoreForHole(markerScores, h.number);
                                const adj   = isSampleData ? 0 : (markerScores.find((s) => s.holeNumber === h.number)?.markerHcpAdj ?? 0);
                                return (
                                    <td key={h.number} style={baseCell(sf, {
                                        backgroundColor: cellBg(COLORS.markerBg, h.number, highlightHole),
                                        ...(score != null ? { fontWeight: 600, color: COLORS.scoreInk } : {}),
                                        ...(rowHeight != null ? { height: rowHeight } : {}),
                                    })}>
                                        {score ?? ''}
                                        {config.showStrokes && adj > 0 && (
                                            <span style={{
                                                fontSize: `${6 * sf}px`,
                                                verticalAlign: 'super',
                                                color: '#c00',
                                                marginLeft: `${1 * sf}px`,
                                            }}>
                                                {strokeDots(adj)}
                                            </span>
                                        )}
                                    </td>
                                );
                            })}
                            {(() => {
                                const outM = isSampleData ? null : sumScores(markerScores, front9);
                                return (
                                    <td style={subtotalStyle(COLORS.subtotalBg, {
                                        ...(outM != null ? { fontWeight: 600, color: COLORS.scoreInk } : {}),
                                        ...(rowHeight != null ? { height: rowHeight } : {}),
                                    })}>
                                        {outM ?? ''}
                                    </td>
                                );
                            })()}
                            {showFold && <FoldGapCell sf={sf} foldGapPx={foldPx} />}
                            {back9.map((h) => {
                                const score = isSampleData ? null : scoreForHole(markerScores, h.number);
                                const adj   = isSampleData ? 0 : (markerScores.find((s) => s.holeNumber === h.number)?.markerHcpAdj ?? 0);
                                return (
                                    <td key={h.number} style={baseCell(sf, {
                                        backgroundColor: cellBg(COLORS.markerBg, h.number, highlightHole),
                                        ...(score != null ? { fontWeight: 600, color: COLORS.scoreInk } : {}),
                                        ...(rowHeight != null ? { height: rowHeight } : {}),
                                    })}>
                                        {score ?? ''}
                                        {config.showStrokes && adj > 0 && (
                                            <span style={{
                                                fontSize: `${6 * sf}px`,
                                                verticalAlign: 'super',
                                                color: '#c00',
                                                marginLeft: `${1 * sf}px`,
                                            }}>
                                                {strokeDots(adj)}
                                            </span>
                                        )}
                                    </td>
                                );
                            })}
                            {back9.length > 0 && (() => {
                                const inM = isSampleData ? null : sumScores(markerScores, back9);
                                return (
                                    <td style={subtotalStyle(COLORS.subtotalBg, {
                                        ...(inM != null ? { fontWeight: 600, color: COLORS.scoreInk } : {}),
                                        ...(rowHeight != null ? { height: rowHeight } : {}),
                                    })}>
                                        {inM ?? ''}
                                    </td>
                                );
                            })()}
                            {(() => {
                                const o = isSampleData ? null : sumScores(markerScores, front9);
                                const i = isSampleData ? null : sumScores(markerScores, back9);
                                const tot = o != null && i != null ? o + i : o ?? i ?? null;
                                return (
                                    <td style={subtotalStyle(COLORS.subtotalBg, {
                                        ...(tot != null ? { fontWeight: 600, color: COLORS.scoreInk } : {}),
                                        ...(rowHeight != null ? { height: rowHeight } : {}),
                                    })}>
                                        {tot ?? ''}
                                    </td>
                                );
                            })()}
                        </tr>
                    )}
                </tbody>
            </table>
        </Box>
    );
};

export default ScorecardGrid;
